import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '@/services/analytics';

/**
 * Human-readable page titles keyed by route path pattern.
 * Falls back to the raw pathname if no match is found.
 */
const PAGE_TITLES: Record<string, string> = {
  '/': 'Command Center',
  '/chart': 'Block Chart',
  '/positions': 'Positions',
  '/positions/new': 'New Position',
  '/members': 'Members',
  '/members/new': 'New Member',
  '/settings': 'Settings',
  '/info': 'Info Hub',
};

function resolveTitle(pathname: string): string {
  // Exact match first
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];

  // Pattern matches for dynamic routes
  if (/^\/positions\/[^/]+\/edit$/.test(pathname)) return 'Edit Position';
  if (/^\/members\/[^/]+\/edit$/.test(pathname)) return 'Edit Member';
  if (/^\/shifts\/[^/]+\/schedule$/.test(pathname)) return 'Shift Schedule';
  if (/^\/schedule\/[^/]+$/.test(pathname)) return 'Schedule Detail';

  return pathname;
}

/**
 * Hook that fires a `page_view` event on every route change.
 * Must be rendered inside a `<BrowserRouter>`.
 */
export function useRouteTracking(): void {
  const location = useLocation();
  const prevPath = useRef<string | null>(null);

  useEffect(() => {
    const currentPath = location.pathname + location.search;

    // Skip duplicate fires (e.g. re-renders with same location)
    if (currentPath === prevPath.current) return;
    prevPath.current = currentPath;

    const title = resolveTitle(location.pathname);
    trackPageView(currentPath, title);
  }, [location]);
}
